import React, { useEffect, useRef } from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import { rem } from 'polished'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formToObject } from 'lib/formToObject'
import { forgotPassword, login } from 'actions/AuthActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import { verticalChildSpacing } from 'components/utils/spacing'
import { Form } from '../common'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import Heading from 'components/Luxkit/Typography/Heading'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { signinModalOpen, userForgotPassword } from 'analytics/eventDefinitions'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Group from 'components/utils/Group'

const StyledForm = styled(Form)`
  margin-bottom: ${rem(320)};
`

const ButtonSection = styled.div`
  text-align: center;
  ${verticalChildSpacing[8]}
`

const ForgetLink = styled(TextLink)`
  margin-top: ${rem(16)};
`

interface LoginForm {
  email: string;
  password: string;
}

interface Props {
  error?: AnyStyledComponent;
  open: boolean;
  email: string;
  isStoreMode: boolean;
  loginStartTestEnabled: boolean;
}

function LoginPassword(props: Props) {
  const {
    error,
    open,
    email,
    isStoreMode,
    loginStartTestEnabled,
  } = props

  const nativeInputRef = useRef<HTMLInputElement>(null)
  const textInputRef = useRef<TextInputOnly>(null)

  useEffect(() => {
    if (open) {
      nativeInputRef.current?.focus()
      fireNonInteractionEvent(signinModalOpen())
    }
  }, [open])

  const dispatch = useAppDispatch()

  const reRef = useRef<ReCAPTCHA>(null)

  const onForgotPassword = () => {
    textInputRef.current?.reset()
    dispatch(forgotPassword(email))
    fireInteractionEvent(userForgotPassword())
  }

  const onLogin = async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const loginData = formToObject<LoginForm>(e.currentTarget)
    let recaptchaData
    if (reRef.current) {
      recaptchaData = await reRef.current.executeAsync()
    }
    dispatch(login(loginData.email, loginData.password, recaptchaData))
  }

  return (
    <StyledForm onSubmit={onLogin} name="loginForm">
      <Group direction="vertical" gap={20}>
        {error?.message && <MessageBanner kind="critical" description={error.message} />}
        {!loginStartTestEnabled && <Heading variant="heading3">Welcome back</Heading>}
      </Group>
      <BodyText variant="large">Please enter your password below to continue.</BodyText>
      <fieldset disabled={!open}>
        <HiddenInput
          id="email"
          name="email"
          value={email}
        />
        <PasswordInput
        label="Password"
        id="password"
        required
        name="password"
        placeholder="Password"
        requiredErrorMessage="Password is required"
        ref={nativeInputRef}
        textInputRef={textInputRef}
      />
        <ButtonSection>
          <TextButton
            kind="primary"
            size="large"
            type="submit"
            fit="flex">
            Login
          </TextButton>
          {!isStoreMode && <ForgetLink
            onClick={onForgotPassword}
            size="medium"
          >
            Forgot your password?
          </ForgetLink>}
        </ButtonSection>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
      </fieldset>
    </StyledForm>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    isStoreMode: state.config.storeMode,
  }
}

export default connect(mapStateToProps)(LoginPassword)
